import React, { ReactNode } from 'react';
import { useConfig } from '../context/ConfigContext';
import { URL_PARAMS } from '../config/runtimeConfig';

interface ConfigLoaderProps {
  children: ReactNode;
}

/**
 * Loading screen shown while runtime configuration is being resolved.
 * Wraps the app and only renders children once config is loaded.
 * Shows error screen if required URL parameters are missing.
 */
export function ConfigLoader({ children }: ConfigLoaderProps) {
  const { isLoading, error, config, missingParams } = useConfig();

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin mb-4" />
          <p className="text-white text-lg">Connecting...</p>
        </div>
      </div>
    );
  }

  if (error || !config) {
    const exampleUrl = `${window.location.origin}${window.location.pathname}?${URL_PARAMS.apiUrl}=http://192.168.1.100:8001&${URL_PARAMS.wsUrl}=ws://192.168.1.100:8002`;

    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black">
        <div className="text-center max-w-2xl px-6">
          <div className="text-red-500 text-5xl mb-6">Configuration Required</div>
          <p className="text-white text-lg mb-4">
            This kiosk app requires backend connection parameters.
          </p>
          {missingParams.length > 0 && (
            <p className="text-red-400 text-sm mb-6">
              Missing: <code className="bg-white/10 px-2 py-1 rounded">{missingParams.join(', ')}</code>
            </p>
          )}
          {error && !missingParams.length && (
            <p className="text-red-400 text-sm mb-6">{error}</p>
          )}
          <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
            <p className="text-white/60 text-xs mb-2">Required URL parameters:</p>
            <ul className="text-white/80 text-sm space-y-1 mb-4">
              <li><code className="text-green-400">apiUrl</code> - REST API endpoint (e.g., http://192.168.1.100:8001)</li>
              <li><code className="text-green-400">wsUrl</code> - WebSocket endpoint (e.g., ws://192.168.1.100:8002)</li>
              <li><code className="text-white/40">kioskId</code> - Optional kiosk identifier</li>
            </ul>
            <p className="text-white/60 text-xs mb-2">Example:</p>
            <code className="text-xs text-blue-400 break-all">{exampleUrl}</code>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default ConfigLoader;
